import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CrudService } from '../../services';
import { useResourceMutations } from '../../hooks';

type FieldType =
  | 'text'
  | 'email'
  | 'number'
  | 'date'
  | 'textarea'
  | 'select'
  | 'async-select';

export interface SelectOption {
  label: string;
  value: string | number;
}

export type FieldValueType = 'string' | 'number' | 'string[]' | 'number[]';

export interface CustomFieldRenderProps {
  value: unknown;
  onChange: (value: unknown) => void;
  field: ResourceField<any>;
  controlId: string;
  formData: Record<string, unknown>;
  setFieldValue?: (name: string, value: unknown) => void;
}

export interface ResourceField<T> {
  name: keyof T | string;
  label: string;
  type?: FieldType;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  options?: SelectOption[];
  loadOptions?: (query: string) => Promise<SelectOption[]>;
  multiple?: boolean;
  valueType?: FieldValueType;
  render?: (props: CustomFieldRenderProps) => React.ReactNode;
  hidden?: boolean;
  fixedValue?: unknown;
  colClass?: string;
  showSearchControl?: boolean;
}

export interface ResourceFormPageProps<T> {
  title: string;
  description?: string;
  fields: ResourceField<T>[];
  service: CrudService<T>;
  redirectPath: string;
  cancelPath?: string;
  initialValues?: Partial<Record<string, unknown>>;
  renderAfterActions?: (
    formData: Record<string, unknown>,
    setFieldValue: (name: string, value: unknown) => void,
  ) => React.ReactNode;
  onSubmitPayload?: (payload: Record<string, unknown>) => void;
  onPayloadChange?: (payload: Record<string, unknown>) => void;
  transformPayload?: (payload: Record<string, unknown>) => Record<string, unknown>;
  renderAfterRecord?: (
    record: Partial<T> | null,
    formData: Record<string, unknown>,
    setFieldValue: (name: string, value: unknown) => void,
  ) => React.ReactNode;
}

const parseValue = (raw: unknown, field?: ResourceField<any>) => {
  const valueType = field?.valueType ?? (field?.multiple ? 'string[]' : 'string');

  if (valueType === 'number') {
    if (raw === '' || raw === undefined || raw === null) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (valueType === 'number[]') {
    const arr = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
    return arr.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
  }

  if (valueType === 'string[]') {
    if (Array.isArray(raw)) return raw.map(String);
    if (raw === undefined || raw === null || raw === '') return [];
    return [String(raw)];
  }

  return raw;
};

const SelectInput: React.FC<{
  field: ResourceField<any>;
  value: unknown;
  onChange: (value: unknown) => void;
  errors?: string[];
  disabled?: boolean;
}> = ({ field, value, onChange, errors, disabled }) => {
  const [options, setOptions] = useState<SelectOption[]>(field.options ?? []);
  const [search, setSearch] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);

  const load = useCallback(
    async (query: string) => {
      if (!field.loadOptions) return;
      setLoadingOptions(true);
      try {
        const loaded = await field.loadOptions(query);
        setOptions(loaded);
      } catch (err) {
        console.error('Erro ao carregar opcoes', err);
      } finally {
        setLoadingOptions(false);
      }
    },
    [field],
  );

  useEffect(() => {
    if (field.loadOptions) {
      load('');
    } else if (field.options) {
      setOptions(field.options);
    }
  }, [field.loadOptions, field.options, load]);

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (field.multiple) {
      const values = Array.from(event.target.selectedOptions).map((opt) => opt.value);
      onChange(values);
    } else {
      onChange(event.target.value);
    }
  };

  return (
    <Form.Group controlId={`resource-form-${String(field.name)}`}>
      <Form.Label>{field.label}</Form.Label>
      {field.loadOptions && field.showSearchControl !== false && (
        <InputGroup className="mb-1">
          <Form.Control
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(event) => {
              const query = event.target.value;
              setSearch(query);
              load(query);
            }}
            disabled={disabled}
          />
          <InputGroup.Text>
            {loadingOptions ? <Spinner animation="border" size="sm" /> : 'Buscar'}
          </InputGroup.Text>
        </InputGroup>
      )}
      <Form.Select
        multiple={field.multiple}
        value={
          field.multiple
            ? ((value as (string | number)[]) ?? []).map((item) => String(item))
            : value === null || value === undefined
            ? ''
            : String(value)
        }
        required={field.required}
        isInvalid={!!errors}
        disabled={disabled || loadingOptions}
        onChange={handleSelectChange}
      >
        {!field.multiple && <option value="">Selecione</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
      {errors && (
        <Form.Control.Feedback type="invalid">{errors.join(', ')}</Form.Control.Feedback>
      )}
      {field.helperText && !errors && <Form.Text>{field.helperText}</Form.Text>}
    </Form.Group>
  );
};

export function ResourceFormPage<T>({
  title,
  description,
  fields,
  service,
  redirectPath,
  cancelPath,
  initialValues,
  renderAfterActions,
  onSubmitPayload,
  onPayloadChange,
  transformPayload,
  renderAfterRecord,
}: ResourceFormPageProps<T>) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const locationState =
    (location.state as { record?: Partial<T> & { id?: number | string } }) || {};
  const [fetchedRecord, setFetchedRecord] = useState<Partial<T> | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const loadingRecordTimerRef = useRef<NodeJS.Timeout | null>(null);
  const record = (fetchedRecord ?? locationState.record) ?? null;
  const recordId = (record as any)?.id ?? params.id;
  const isEdit = recordId !== undefined && recordId !== null;
  const { create, update, loading, error, validationErrors } = useResourceMutations(service);

  useEffect(() => {
    const id = params.id;
    if (!record && id) {
      loadingRecordTimerRef.current = setTimeout(() => setLoadingRecord(true), 100);
      service
        .retrieve(id)
        .then((data) => setFetchedRecord(data))
        .catch((err: any) => {
          if (err?.response?.status === 404) {
            navigate('/admin/404', { replace: true });
            return;
          }
          console.error('Erro ao carregar registro', err);
        })
        .finally(() => {
          if (loadingRecordTimerRef.current) {
            clearTimeout(loadingRecordTimerRef.current);
            loadingRecordTimerRef.current = null;
          }
          setLoadingRecord(false);
        });
    }
  }, [params.id, record, service, navigate]);

  const initialState = useMemo(
    () =>
      fields.reduce<Record<string, unknown>>((acc, field) => {
        const key = field.name as string;
        const baseValue =
          field.fixedValue !== undefined
            ? field.fixedValue
            : (record && (record as Record<string, unknown>)[key]) ??
              (initialValues ? initialValues[key] : undefined);
        if (field.valueType === 'number') {
          acc[key] = baseValue ?? null;
        } else if (field.valueType === 'number[]' || field.multiple || field.valueType === 'string[]') {
          acc[key] = baseValue ?? [];
        } else {
          acc[key] = baseValue ?? '';
        }
        return acc;
      }, {}),
    [fields, initialValues, record],
  );

  const [formData, setFormData] = useState<Record<string, unknown>>(initialState);

  useEffect(() => {
    setFormData(initialState);
  }, [initialState]);

  const fieldsByName = useMemo(() => {
    return new Map(fields.map((field) => [field.name as string, field]));
  }, [fields]);

  const buildPayload = useCallback(
    (data: Record<string, unknown>) =>
      fields.reduce<Record<string, unknown>>((acc, field) => {
        if (field.fixedValue !== undefined) {
          acc[field.name as string] = parseValue(field.fixedValue, field);
        }
        return acc;
      }, { ...data }),
    [fields],
  );

  const handleChange = (name: string, rawValue: unknown, field?: ResourceField<any>) => {
    if (field?.fixedValue !== undefined) {
      return;
    }
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: parseValue(rawValue, field),
      };
      if (onPayloadChange) {
        onPayloadChange(buildPayload(next));
      }
      return next;
    });
  };

  const setFieldValue = useCallback(
    (name: string, rawValue: unknown) => {
      const targetField = fieldsByName.get(name);
      handleChange(name, rawValue, targetField);
    },
    [fieldsByName, handleChange],
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      let payload = buildPayload(formData);
      if (transformPayload) {
        payload = transformPayload(payload);
      }
      if (onSubmitPayload) {
        onSubmitPayload(payload);
      }
      if (isEdit) {
        await update(recordId as any, payload as Partial<T>);
      } else {
        await create(payload as Partial<T>);
      }
      navigate(redirectPath);
    } catch {
      // erros tratados pelo hook
    }
  };

  const showForm = !isEdit || !loadingRecord;

  const bodyContent = (
    // @ts-ignore React 19 JSX tipagem exige children único
    <div className="d-flex flex-column gap-4">
      {loadingRecord && isEdit && (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Carregando registro...</span>
        </div>
      )}
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <h5 className="mb-1 mb-lg-0">{title}</h5>
        <span className={`badge ${isEdit ? 'bg-warning text-dark' : 'bg-primary'}`}>
          {isEdit ? 'Editando' : 'Novo'}
        </span>
      </div>
      {description && <p className="text-muted mb-0">{description}</p>}

      {error && (
        <Alert variant="danger">
          {(() => {
            if (error && typeof error === 'object') {
              const err = error as any;
              if (err.response?.data?.message) {
                return err.response.data.message;
              }
              if (err.message) {
                return err.message;
              }
            }
            return 'Não foi possível salvar. Verifique os dados e tente novamente.';
          })()}
        </Alert>
      )}
      {showForm && (
        <Form onSubmit={handleSubmit}>
        <div className="row g-3">
          {fields.map((field, idx) => {
            const name = field.name as string;
            const value = formData[name] ?? '';
            const controlId = `resource-form-${name}`;
            const type = field.type ?? 'text';
            const defaultColClass =
              'col-12'; // cada campo ocupa a linha inteira por padrão
            const colClass = field.colClass ?? defaultColClass;

            if (field.hidden) {
              return null;
            }

            if (field.render) {
              return (
                <div className={colClass} key={name}>
                  {field.render({
                    value,
                    onChange: (val) => handleChange(name, val, field),
                    field,
                    controlId,
                    formData,
                    setFieldValue,
                  })}
                </div>
              );
            }

            if (type === 'textarea') {
              const fieldErrors = validationErrors?.[name];
              return (
                <div className={colClass} key={name}>
                  <Form.Group controlId={controlId}>
                    <Form.Label>{field.label}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={(value as string) ?? ''}
                      placeholder={field.placeholder}
                      required={field.required}
                      isInvalid={!!fieldErrors}
                      disabled={loadingRecord}
                      onChange={(event) => handleChange(name, event.target.value, field)}
                    />
                    {fieldErrors && (
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.join(', ')}
                      </Form.Control.Feedback>
                    )}
                    {field.helperText && !fieldErrors && <Form.Text>{field.helperText}</Form.Text>}
                  </Form.Group>
                </div>
              );
            }

            if (type === 'select' || type === 'async-select') {
              const fieldErrors = validationErrors?.[name];
              return (
                <div className={colClass} key={name}>
                  <SelectInput
                    field={field}
                    value={value}
                    onChange={(val) => handleChange(name, val, field)}
                    errors={fieldErrors}
                    disabled={loadingRecord}
                  />
                </div>
              );
            }

            const fieldErrors = validationErrors?.[name];
            return (
              <div className={colClass} key={name}>
                <Form.Group controlId={controlId}>
                  <Form.Label>{field.label}</Form.Label>
                  <Form.Control
                    type={type}
                    value={value === null || value === undefined ? '' : (value as string | number)}
                    placeholder={field.placeholder}
                    required={field.required}
                    isInvalid={!!fieldErrors}
                    disabled={loadingRecord}
                    onChange={(event) => handleChange(name, event.target.value, field)}
                  />
                  {fieldErrors && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.join(', ')}
                    </Form.Control.Feedback>
                  )}
                  {field.helperText && !fieldErrors && <Form.Text>{field.helperText}</Form.Text>}
                </Form.Group>
              </div>
            );
          })}

          {renderAfterActions && (
            <div className="col-12 mt-4">{renderAfterActions(formData, setFieldValue)}</div>
          )}
          {renderAfterRecord && (
            <div className="col-12">{renderAfterRecord(record, formData, setFieldValue)}</div>
          )}
          <div className="col-12 d-flex justify-content-end gap-2">
            {cancelPath && (
              <Button
                type="button"
                variant="outline-secondary"
                onClick={() => navigate(cancelPath)}
                disabled={loading}
              >
                Cancelar
              </Button>
            )}
            <Button type="submit" disabled={loading || loadingRecord}>
              {loading
                ? isEdit
                  ? 'Atualizando...'
                  : 'Salvando...'
                : isEdit
                ? 'Atualizar'
                : 'Salvar'}
            </Button>
          </div>
        </div>
        </Form>
      )}
    </div>
  );

  return React.createElement(
    'div',
    { className: 'card shadow-sm border-0' },
    React.createElement('div', { className: 'card-body pb-6' }, bodyContent),
  );
}
